import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import { ConferenceOrderItem, ConferenceOrderSource } from '../../SellerQueue/types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

type OpportunitySource = ConferenceOrderSource | 'balanced'
type OpportunityProfile = 'expand' | 'recover' | 'monitor'

interface OpportunityRow {
  sellerKey: string
  sellerLabel: string
  totalOrders: number
  totalAmountCents: number
  ninegridAmountCents: number
  totvsAmountCents: number
  ninegridSharePercent: number
  totvsSharePercent: number
  dependencySource: OpportunitySource
  profile: OpportunityProfile
  opportunityScore: number
  recommendation: string
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const readString = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

const getSellerKeyAndLabel = (order: ConferenceOrderItem): { sellerKey: string; sellerLabel: string } => {
  const content = isRecord(order.content) ? order.content : {}
  const rawTotvsOrder = isRecord(content.rawTotvsOrder) ? content.rawTotvsOrder : {}
  const rawNinegridOrder = isRecord(content.rawNinegridOrder) ? content.rawNinegridOrder : {}
  const commissioneds = Array.isArray(rawTotvsOrder.commissioneds) ? rawTotvsOrder.commissioneds : []
  const firstCommissioned = isRecord(commissioneds[0]) ? commissioneds[0] : {}

  const sellerNameCandidates = [
    content.sellerName,
    rawNinegridOrder.sellerName,
    rawTotvsOrder.sellerName,
    firstCommissioned.sellerName,
    rawTotvsOrder.representativeName,
  ]

  const sellerCodeCandidates = [
    content.sellerCode,
    rawNinegridOrder.sellerCode,
    rawTotvsOrder.sellerCode,
    firstCommissioned.sellerCode,
    rawTotvsOrder.representativeCode,
  ]

  const sellerCpfCandidates = [
    content.sellerCpf,
    rawNinegridOrder.sellerCpf,
    rawTotvsOrder.sellerCpfCnpj,
    firstCommissioned.sellerCpfCnpj,
  ]

  const sellerName = sellerNameCandidates.map(readString).find(Boolean) || ''
  const sellerCode = sellerCodeCandidates.map(readString).find(Boolean) || ''
  const sellerCpf = sellerCpfCandidates.map(readString).find(Boolean) || ''

  const sellerLabel = sellerName || sellerCode || sellerCpf || 'Sem vendedor'
  const sellerKey = sellerCode || sellerCpf || sellerName || 'sem-vendedor'

  return {
    sellerKey,
    sellerLabel,
  }
}

const buildOpportunityRows = (report: SellerTicketAverageReport | null): OpportunityRow[] => {
  if (!report?.items.length) {
    return []
  }

  const enrichedLabelBySellerKey = new Map(report.rows.map((row) => [row.sellerKey, row.sellerLabel]))
  const grouped = new Map<
    string,
    Omit<
      OpportunityRow,
      | 'ninegridSharePercent'
      | 'totvsSharePercent'
      | 'dependencySource'
      | 'profile'
      | 'opportunityScore'
      | 'recommendation'
    >
  >()

  for (const order of report.items) {
    const source = order.source
    if (source !== 'ninegrid' && source !== 'totvs') {
      continue
    }

    const { sellerKey, sellerLabel } = getSellerKeyAndLabel(order)
    const current = grouped.get(sellerKey) || {
      sellerKey,
      sellerLabel: enrichedLabelBySellerKey.get(sellerKey) || sellerLabel,
      totalOrders: 0,
      totalAmountCents: 0,
      ninegridAmountCents: 0,
      totvsAmountCents: 0,
    }

    const orderTotal = typeof order.total === 'number' ? Math.round(order.total) : 0

    current.sellerLabel = enrichedLabelBySellerKey.get(sellerKey) || sellerLabel || current.sellerLabel
    current.totalOrders += 1
    current.totalAmountCents += orderTotal

    if (source === 'ninegrid') {
      current.ninegridAmountCents += orderTotal
    } else {
      current.totvsAmountCents += orderTotal
    }

    grouped.set(sellerKey, current)
  }

  return Array.from(grouped.values())
    .map((row) => {
      const ninegridSharePercent =
        row.totalAmountCents > 0 ? (row.ninegridAmountCents / row.totalAmountCents) * 100 : 0
      const totvsSharePercent =
        row.totalAmountCents > 0 ? (row.totvsAmountCents / row.totalAmountCents) * 100 : 0

      let dependencySource: OpportunitySource = 'balanced'
      if (ninegridSharePercent > totvsSharePercent) {
        dependencySource = 'ninegrid'
      } else if (totvsSharePercent > ninegridSharePercent) {
        dependencySource = 'totvs'
      }

      const highestSharePercent = Math.max(ninegridSharePercent, totvsSharePercent)
      const balanceDistance = Math.abs(ninegridSharePercent - 50)

      let profile: OpportunityProfile = 'monitor'
      let recommendation = 'Acompanhar mix atual e preservar consistencia operacional.'
      let opportunityScore = 0

      if (highestSharePercent < 70) {
        profile = 'expand'
        recommendation = 'Base equilibrada. Priorizar expansão comercial e replicar boas práticas nos dois canais.'
        opportunityScore = Math.round((row.totalAmountCents / 10000) + Math.max(0, 25 - balanceDistance))
      } else if (highestSharePercent >= 85) {
        profile = 'recover'
        recommendation =
          dependencySource === 'ninegrid'
            ? 'Alta dependência de Ninegrid. Acionar plano comercial para acelerar uso de TOTVS.'
            : 'Alta dependência de TOTVS. Acionar plano comercial para acelerar uso de Ninegrid.'
        opportunityScore = Math.round((row.totalAmountCents / 10000) + highestSharePercent)
      } else {
        recommendation = 'Dependência moderada. Monitorar e testar a ampliação gradual do canal menos usado.'
        opportunityScore = Math.round((row.totalAmountCents / 20000) + highestSharePercent / 2)
      }

      return {
        ...row,
        ninegridSharePercent,
        totvsSharePercent,
        dependencySource,
        profile,
        opportunityScore,
        recommendation,
      }
    })
    .sort((left, right) => right.opportunityScore - left.opportunityScore || right.totalAmountCents - left.totalAmountCents)
}

const profileLabel = (profile: OpportunityProfile): string => {
  if (profile === 'expand') {
    return 'Expandir'
  }

  if (profile === 'recover') {
    return 'Migrar canal'
  }

  return 'Monitorar'
}

const profileBadgeClass = (profile: OpportunityProfile): string => {
  if (profile === 'expand') {
    return 'badge badge-light-success'
  }

  if (profile === 'recover') {
    return 'badge badge-light-danger'
  }

  return 'badge badge-light-warning'
}

const dependencyLabel = (source: OpportunitySource): string => {
  if (source === 'ninegrid') {
    return 'Ninegrid'
  }

  if (source === 'totvs') {
    return 'TOTVS'
  }

  return 'Equilibrado'
}

const exportToCSV = (filename: string, rows: OpportunityRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'Vendedor;Pedidos;Faturamento;Ninegrid (%);TOTVS (%);Perfil;Score;Recomendacao\n'

  rows.forEach((row) => {
    csv += `"${row.sellerLabel}";${row.totalOrders};${row.totalAmountCents / 100};${row.ninegridSharePercent.toFixed(1)};${row.totvsSharePercent.toFixed(1)};${profileLabel(row.profile)};${row.opportunityScore};"${row.recommendation}"\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: OpportunityRow[]): void => {
  const workbook = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(
    rows.map((row) => ({
      Vendedor: row.sellerLabel,
      Pedidos: row.totalOrders,
      Faturamento: row.totalAmountCents / 100,
      'Ninegrid (%)': row.ninegridSharePercent.toFixed(1),
      'TOTVS (%)': row.totvsSharePercent.toFixed(1),
      Perfil: profileLabel(row.profile),
      Score: row.opportunityScore,
      Recomendacao: row.recommendation,
    }))
  )

  XLSX.utils.book_append_sheet(workbook, sheet, 'Oportunidades')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, rows: OpportunityRow[]): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Oportunidade de Migração', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Resumo', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Vendedores analisados: ${rows.length} | Expandir: ${rows.filter((row) => row.profile === 'expand').length} | Migrar canal: ${rows.filter((row) => row.profile === 'recover').length}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Vendedor', 'Pedidos', 'Faturamento', 'Ninegrid (%)', 'TOTVS (%)', 'Perfil', 'Score']],
    body: rows.map((row) => [
      row.sellerLabel,
      row.totalOrders,
      formatCurrency(row.totalAmountCents),
      row.ninegridSharePercent.toFixed(1),
      row.totvsSharePercent.toFixed(1),
      profileLabel(row.profile),
      row.opportunityScore,
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerMigrationOpportunityReportPage: FC = () => {
  const [filters, setFilters] = useState<SellerTicketAverageFilters>({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatorio',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const rows = useMemo(() => buildOpportunityRows(report), [report])
  const expandRows = useMemo(() => rows.filter((row) => row.profile === 'expand'), [rows])
  const recoverRows = useMemo(() => rows.filter((row) => row.profile === 'recover'), [rows])
  const topOpportunity = rows[0] || null

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Oportunidade de Migração',
            path: '/seller/reports/migration-opportunity',
            isActive: true,
          },
        ]}
      >
        Relatório de Oportunidade de Migração
      </PageTitle>

      <ReportShell
        title='Oportunidade de Migração por Vendedor'
        description='Separa vendedores balanceados dos excessivamente dependentes para orientar a ação comercial de expansão ou recuperação de canal.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Oportunidade-Migracao-${timestamp}.csv`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Oportunidade-Migracao-${timestamp}.xlsx`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Oportunidade-Migracao-${timestamp}.pdf`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Perfis para expandir</div>
                <div className='fs-2hx fw-bold text-success'>
                  {expandRows.length.toLocaleString('pt-BR')}
                </div>
                <div className='text-gray-600 mt-2'>Base equilibrada</div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Perfis para recuperar canal</div>
                <div className='fs-2hx fw-bold text-danger'>
                  {recoverRows.length.toLocaleString('pt-BR')}
                </div>
                <div className='text-gray-600 mt-2'>Dependencia alta</div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior oportunidade</div>
                <div className='fs-5 fw-bold text-primary'>
                  {topOpportunity ? <SellerIdentity label={topOpportunity.sellerLabel} /> : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-primary'>
                  {topOpportunity ? `Score ${topOpportunity.opportunityScore}` : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Plano de ação comercial</span>
              <span className='text-gray-600 mt-1'>Score, perfil recomendado e observação para cada vendedor.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : rows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Vendedor</th>
                      <th className='text-end'>Score</th>
                      <th className='text-end'>Perfil</th>
                      <th className='text-end'>Canal dominante</th>
                      <th className='text-end'>Ninegrid</th>
                      <th className='text-end'>TOTVS</th>
                      <th className='text-end'>Faturamento</th>
                      <th>Recomendacao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.sellerKey}>
                        <td>
                          <div className='fw-bold text-dark'>
                            <SellerIdentity label={row.sellerLabel} />
                          </div>
                        </td>
                        <td className='text-end fw-bold text-primary'>{row.opportunityScore}</td>
                        <td className='text-end'>
                          <span className={profileBadgeClass(row.profile)}>{profileLabel(row.profile)}</span>
                        </td>
                        <td className='text-end'>{dependencyLabel(row.dependencySource)}</td>
                        <td className='text-end text-primary'>
                          {percentFormatter.format(row.ninegridSharePercent)}%
                        </td>
                        <td className='text-end text-warning'>
                          {percentFormatter.format(row.totvsSharePercent)}%
                        </td>
                        <td className='text-end fw-bold text-success'>
                          {formatCurrency(row.totalAmountCents)}
                        </td>
                        <td>
                          <span className='text-gray-700'>{row.recommendation}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum vendedor encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { SellerMigrationOpportunityReportPage }